import type { PanderrSection } from "../types";
import { renderPanderrButton } from "../components/button";
import { renderSiteFieldAttribute } from "../components/site-binding";

function escapeHtml(value: string): string {
  return String(value || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

export function renderTrustSection(section: PanderrSection): string {
  return `
    <section class="panderr-trust">
      <div class="panderr-shell-wide panderr-trust-grid">
        <span>Trusted expertise</span>
        <span>Clear communication</span>
        <span>Thoughtful solutions</span>
        <span>Professional results</span>
      </div>
    </section>
  `;
}

export function renderServicesSection(section: PanderrSection): string {
  const services = section.services ?? [];

  return `
    <section
      class="panderr-section panderr-services"
      data-panderr-section-id="${escapeHtml(section.id)}"
    >
      <div class="panderr-shell-wide">
        <div class="panderr-section-heading">
          <div>
            <p class="panderr-eyebrow">${escapeHtml(section.eyebrow || "")}</p>
            <h2>${escapeHtml(section.heading || "")}</h2>
          </div>

          <p class="panderr-lead">${escapeHtml(section.body || "")}</p>
        </div>

        <div class="panderr-service-grid">
          ${services
            .map(
              (service, index) => `
            <article class="panderr-service-card" data-panderr-element="card">
              <span class="panderr-service-number">0${index + 1}</span>
              <h3>${escapeHtml(service.title)}</h3>
              <p>${escapeHtml(service.description)}</p>
              <a href="${escapeHtml(service.href || "/services/")}">
                Learn more →
              </a>
            </article>
          `,
            )
            .join("")}
        </div>
      </div>
    </section>
  `;
}

export function renderAboutSection(section: PanderrSection): string {
  const image = section.images?.[0];

  return `
    <section class="panderr-section panderr-about">
      <div class="panderr-shell-wide panderr-about-grid">
        ${
          image
            ? `
          <div class="panderr-about-media">
            <img
              src="${escapeHtml(image.src)}"
              alt="${escapeHtml(image.alt)}"
              data-panderr-component="image"
              data-panderr-image-role="about"
            >
          </div>
        `
            : ""
        }

        <div class="panderr-about-content">
          <p class="panderr-eyebrow">${escapeHtml(section.eyebrow || "")}</p>
          <h2>${escapeHtml(section.heading || "")}</h2>
          <p class="panderr-lead">${escapeHtml(section.body || "")}</p>

          <a
            class="panderr-button panderr-button-secondary"
            href="/about/"
          >
            Learn About Us
          </a>
        </div>
      </div>
    </section>
  `;
}

export function renderCtaSection(section: PanderrSection): string {
  return `
    <section class="panderr-section panderr-cta-section">
      <div class="panderr-shell-wide">
        <div class="panderr-cta-card" data-panderr-element="card">
          <div>
            <p class="panderr-eyebrow">${escapeHtml(section.eyebrow || "")}</p>
            <h2>${escapeHtml(section.heading || "")}</h2>
            <p>${escapeHtml(section.body || "")}</p>
          </div>

          <a
            class="panderr-button panderr-button-primary"
            href="/contact/"
          >
            Start a Conversation
          </a>
        </div>
      </div>
    </section>
  `;
}

function renderSectionButtons(section: PanderrSection): string {
  const buttons = section.buttons ?? [];

  if (!buttons.length) return "";

  return `
    <div class="panderr-section-actions">
      ${buttons.map(renderPanderrButton).join("")}
    </div>
  `;
}

function renderStandardHeading(section: PanderrSection): string {
  return `
    <div class="panderr-section-heading">
      <div>
        ${
          section.eyebrow
            ? `<p class="panderr-eyebrow"${renderSiteFieldAttribute(section.eyebrowField)}>${escapeHtml(section.eyebrow)}</p>`
            : ""
        }
        ${section.heading || section.headingField ? `<h2${renderSiteFieldAttribute(section.headingField)}>${escapeHtml(section.heading || "")}</h2>` : ""}
      </div>

      ${
        section.body
          ? `<p class="panderr-lead"${renderSiteFieldAttribute(section.bodyField)}>${escapeHtml(section.body)}</p>`
          : ""
      }
    </div>
  `;
}

export function renderProcessSection(section: PanderrSection): string {
  const steps = section.steps?.length
    ? section.steps
    : [
        {
          title: "Start the conversation",
          description: "Tell us what you need and what you are working toward.",
        },
        {
          title: "Review the plan",
          description:
            "Get clear next steps, expectations, and recommendations.",
        },
        {
          title: "Move forward",
          description:
            "Put the plan into action with dependable communication.",
        },
      ];

  return `
    <section class="panderr-section panderr-process">
      <div class="panderr-shell-wide">
        ${renderStandardHeading(section)}

        <div class="panderr-process-grid">
          ${steps
            .map(
              (step, index) => `
                <article class="panderr-process-step" data-panderr-element="card">
                  <span>0${index + 1}</span>
                  <h3>${escapeHtml(step.title)}</h3>
                  <p>${escapeHtml(step.description)}</p>
                </article>
              `,
            )
            .join("")}
        </div>

        ${renderSectionButtons(section)}
      </div>
    </section>
  `;
}

export function renderProjectsSection(section: PanderrSection): string {
  const projects = section.projects ?? [];

  return `
    <section class="panderr-section panderr-projects">
      <div class="panderr-shell-wide">
        ${renderStandardHeading(section)}

        <div class="panderr-project-grid">
          ${projects
            .map(
              (project) => `
                <article class="panderr-project-card" data-panderr-element="card">
                  ${
                    project.image
                      ? `
                        <div class="panderr-project-media">
                          <img
                            src="${escapeHtml(project.image.src)}"
                            alt="${escapeHtml(project.image.alt)}"
                            data-panderr-component="image"
                            data-panderr-image-role="project"
                          >
                        </div>
                      `
                      : ""
                  }

                  <div class="panderr-project-content">
                    <h3>${escapeHtml(project.title)}</h3>
                    ${
                      project.description
                        ? `<p>${escapeHtml(project.description)}</p>`
                        : ""
                    }
                    ${
                      project.href
                        ? `<a href="${escapeHtml(project.href)}">View project →</a>`
                        : ""
                    }
                  </div>
                </article>
              `,
            )
            .join("")}
        </div>

        ${renderSectionButtons(section)}
      </div>
    </section>
  `;
}

export function renderTestimonialsSection(section: PanderrSection): string {
  const testimonials = section.testimonials ?? [];

  return `
    <section class="panderr-section panderr-testimonials">
      <div class="panderr-shell-wide">
        ${renderStandardHeading(section)}

        <div class="panderr-testimonial-grid">
          ${testimonials
            .map(
              (testimonial) => `
                <figure class="panderr-testimonial-card" data-panderr-element="card">
                  <blockquote>
                    “${escapeHtml(testimonial.quote)}”
                  </blockquote>
                  <figcaption>
                    <strong>${escapeHtml(testimonial.name)}</strong>
                    ${
                      testimonial.detail
                        ? `<span>${escapeHtml(testimonial.detail)}</span>`
                        : ""
                    }
                  </figcaption>
                </figure>
              `,
            )
            .join("")}
        </div>
      </div>
    </section>
  `;
}

export function renderServiceAreaSection(section: PanderrSection): string {
  return `
    <section class="panderr-section panderr-service-area">
      <div class="panderr-shell-wide panderr-feature-panel" data-panderr-element="panel">
        <div>
          ${
            section.eyebrow
              ? `<p class="panderr-eyebrow">${escapeHtml(section.eyebrow)}</p>`
              : ""
          }
          ${section.heading ? `<h2>${escapeHtml(section.heading)}</h2>` : ""}
        </div>

        <div>
          ${
            section.body
              ? `<p class="panderr-lead">${escapeHtml(section.body)}</p>`
              : ""
          }
          ${renderSectionButtons(section)}
        </div>
      </div>
    </section>
  `;
}

export function renderContactSection(section: PanderrSection): string {
  return `
    <section class="panderr-section panderr-contact">
      <div class="panderr-shell-wide panderr-contact-panel" data-panderr-element="panel">
        <div>
          ${
            section.eyebrow || section.eyebrowField
              ? `<p class="panderr-eyebrow"${renderSiteFieldAttribute(section.eyebrowField)}>${escapeHtml(section.eyebrow || "")}</p>`
              : ""
          }
          ${
            section.heading || section.headingField
              ? `<h2${renderSiteFieldAttribute(section.headingField)}>${escapeHtml(section.heading || "")}</h2>`
              : ""
          }
        </div>

        <div>
          ${
            section.body || section.bodyField
              ? `<p class="panderr-lead"${renderSiteFieldAttribute(section.bodyField)}>${escapeHtml(section.body || "")}</p>`
              : ""
          }
          ${renderSectionButtons(section)}
        </div>
      </div>
    </section>
  `;
}

export function renderGallerySection(section: PanderrSection): string {
  const images = section.images ?? [];

  return `
    <section class="panderr-section panderr-gallery">
      <div class="panderr-shell-wide">
        ${renderStandardHeading(section)}

        <div class="panderr-gallery-grid">
          ${images
            .map(
              (image) => `
                <figure class="panderr-gallery-item" data-panderr-element="media">
                  <img
                    src="${escapeHtml(image.src)}"
                    alt="${escapeHtml(image.alt)}"
                    data-panderr-component="image"
                    data-panderr-image-role="${escapeHtml(image.role)}"
                  >
                </figure>
              `,
            )
            .join("")}
        </div>
      </div>
    </section>
  `;
}

export function renderFaqSection(section: PanderrSection): string {
  const faqs = section.faqs ?? [];

  return `
    <section class="panderr-section panderr-faq">
      <div class="panderr-shell-wide">
        ${renderStandardHeading(section)}

        ${
          faqs.length
            ? `
              <div class="panderr-faq-list">
                ${faqs
                  .map(
                    (faq) => `
                      <details class="panderr-faq-item" data-panderr-element="item">
                        <summary>${escapeHtml(faq.question)}</summary>
                        <p>${escapeHtml(faq.answer)}</p>
                      </details>
                    `,
                  )
                  .join("")}
              </div>
            `
            : ""
        }
      </div>
    </section>
  `;
}

export function createBodyCss(): string {
  return `
    .panderr-trust {
      border-top: 1px solid var(--panderr-color-border);
      border-bottom: 1px solid var(--panderr-color-border);
    }

    .panderr-trust-grid {
      min-height: 92px;
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      align-items: center;
    }

    .panderr-trust-grid span {
      padding: 1.25rem;
      text-align: center;
      font-size: .85rem;
      font-weight: 700;
      color: var(--panderr-color-text-muted);
    }

    .panderr-section-heading {
      display: grid;
      grid-template-columns: 1.05fr .75fr;
      gap: 4rem;
      align-items: end;
      margin-bottom: 3.5rem;
    }

    .panderr-section-heading h2,
    .panderr-about h2 {
      max-width: 12ch;
    }

    .panderr-service-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      border-top: 1px solid var(--panderr-color-border);
    }

    .panderr-service-card {
      padding: 2.5rem 2rem 2.5rem 0;
      border-right: 1px solid var(--panderr-color-border);
    }

    .panderr-service-card + .panderr-service-card {
      padding-left: 2rem;
    }

    .panderr-service-card:last-child {
      border-right: 0;
    }

    .panderr-service-number {
      display: block;
      margin-bottom: 4rem;
      color: var(--panderr-color-text-muted);
      font-size: .8rem;
      font-weight: 700;
    }

    .panderr-service-card h3 {
      margin-bottom: .85rem;
    }

    .panderr-service-card p {
      color: var(--panderr-color-text-muted);
      margin-bottom: 1.5rem;
    }

    .panderr-service-card a {
      font-weight: 700;
    }

    .panderr-about {
      background: var(--panderr-color-surface);
    }

    .panderr-about-grid {
      display: grid;
      grid-template-columns: 1.05fr .95fr;
      gap: clamp(3rem, 7vw, 7rem);
      align-items: center;
    }

    .panderr-about-media {
      overflow: hidden;
      border-radius: var(--panderr-radius-large);
      min-height: 560px;
    }

    .panderr-about-media img {
      width: 100%;
      height: 100%;
      min-height: 560px;
      object-fit: cover;
    }

    .panderr-about-content .panderr-lead {
      margin: 1.5rem 0 2rem;
    }

    .panderr-cta-card {
      display: flex;
      justify-content: space-between;
      gap: 4rem;
      align-items: flex-end;
      padding: clamp(2.5rem, 6vw, 5rem);
      border-radius: var(--panderr-radius-large);
      background: var(--panderr-color-text);
      color: white;
    }

    .panderr-cta-card h2 {
      max-width: 13ch;
      color: white;
    }

    .panderr-cta-card p:not(.panderr-eyebrow) {
      max-width: 56ch;
      margin-top: 1.25rem;
      color: rgba(255,255,255,.68);
    }

    .panderr-cta-card .panderr-eyebrow {
      color: rgba(255,255,255,.6);
    }

    .panderr-section-actions {
      display: flex;
      flex-wrap: wrap;
      gap: .75rem;
      margin-top: 2rem;
    }

    .panderr-process-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      border-top: 1px solid var(--panderr-color-border);
    }

    .panderr-process-step {
      padding: 2rem;
      border-right: 1px solid var(--panderr-color-border);
    }

    .panderr-process-step:first-child {
      padding-left: 0;
    }

    .panderr-process-step:last-child {
      border-right: 0;
    }

    .panderr-process-step > span {
      display: block;
      margin-bottom: 3rem;
      color: var(--panderr-color-text-muted);
      font-size: .8rem;
      font-weight: 800;
    }

    .panderr-process-step p,
    .panderr-project-content p,
    .panderr-testimonial-card span {
      color: var(--panderr-color-text-muted);
    }

    .panderr-process-step h3,
    .panderr-project-content h3 {
      margin-bottom: .8rem;
    }

    .panderr-project-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 1.5rem;
    }

    .panderr-project-card {
      overflow: hidden;
      border: 1px solid var(--panderr-color-border);
      border-radius: var(--panderr-radius-medium);
      background: var(--panderr-color-background);
    }

    .panderr-project-media {
      aspect-ratio: 4 / 3;
      overflow: hidden;
    }

    .panderr-project-media img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .panderr-project-content {
      padding: 1.5rem;
    }

    .panderr-project-content a {
      display: inline-block;
      margin-top: 1.25rem;
      font-weight: 700;
    }

    .panderr-testimonials {
      background: var(--panderr-color-surface);
    }

    .panderr-testimonial-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 1.5rem;
    }

    .panderr-testimonial-card {
      margin: 0;
      padding: 2rem;
      border: 1px solid var(--panderr-color-border);
      border-radius: var(--panderr-radius-medium);
      background: var(--panderr-color-background);
    }

    .panderr-testimonial-card blockquote {
      margin: 0 0 2rem;
      font-size: 1.25rem;
      line-height: 1.5;
    }

    .panderr-testimonial-card figcaption {
      display: grid;
      gap: .25rem;
    }

    .panderr-feature-panel,
    .panderr-contact-panel {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: clamp(2rem, 6vw, 6rem);
      padding: clamp(2rem, 5vw, 4.5rem);
      border: 1px solid var(--panderr-color-border);
      border-radius: var(--panderr-radius-large);
      background: var(--panderr-color-surface);
    }

    .panderr-feature-panel h2,
    .panderr-contact-panel h2 {
      max-width: 12ch;
    }

    .panderr-gallery-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 1rem;
    }

    .panderr-gallery-item {
      margin: 0;
      overflow: hidden;
      border-radius: var(--panderr-radius-medium);
      aspect-ratio: 4 / 3;
    }

    .panderr-gallery-item img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .panderr-faq-list {
      max-width: 900px;
      border-top: 1px solid var(--panderr-color-border);
    }

    .panderr-faq-item {
      border-bottom: 1px solid var(--panderr-color-border);
    }

    .panderr-faq-item summary {
      padding: 1.4rem 0;
      cursor: pointer;
      font-weight: 800;
      font-size: 1.05rem;
    }

    .panderr-faq-item p {
      max-width: 70ch;
      padding: 0 0 1.5rem;
      color: var(--panderr-color-text-muted);
    }

    @media (max-width: 820px) {
      .panderr-trust-grid {
        grid-template-columns: repeat(2, 1fr);
      }

      .panderr-section-heading,
      .panderr-about-grid {
        grid-template-columns: 1fr;
        gap: 2.5rem;
      }

      .panderr-service-grid {
        grid-template-columns: 1fr;
      }

      .panderr-service-card,
      .panderr-service-card + .panderr-service-card {
        padding: 2rem 0;
        border-right: 0;
        border-bottom: 1px solid var(--panderr-color-border);
      }

      .panderr-service-number {
        margin-bottom: 2rem;
      }

      .panderr-cta-card {
        flex-direction: column;
        align-items: flex-start;
      }

      .panderr-process-grid,
      .panderr-project-grid,
      .panderr-testimonial-grid,
      .panderr-gallery-grid,
      .panderr-feature-panel,
      .panderr-contact-panel {
        grid-template-columns: 1fr;
      }

      .panderr-process-step,
      .panderr-process-step:first-child {
        padding: 2rem 0;
        border-right: 0;
        border-bottom: 1px solid var(--panderr-color-border);
      }

      .panderr-process-step:last-child {
        border-bottom: 0;
      }
    }
  `;
}
